import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import { Check, X, FileText, Loader2, Copy } from "lucide-react";

const TABS = ["pending", "approved", "rejected", "all"];

export default function AdminPartners() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    const params = tab === "all" ? {} : { status: tab };
    api.get("/admin/partners", { params }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/partners/${id}/approve`);
      toast.success("Partner approved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to approve");
    } finally { setBusyId(null); }
  };

  const reject = async (id) => {
    const reason = window.prompt("Reason for rejection (optional):") || "";
    setBusyId(id);
    try {
      await api.post(`/admin/partners/${id}/reject`, { reason });
      toast.success("Application rejected");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to reject");
    } finally { setBusyId(null); }
  };

  const viewPan = async (id) => {
    try {
      const { data } = await api.get(`/admin/partners/${id}/pan-card`, { responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank");
    } catch (e) {
      toast.error("Could not load PAN card");
    }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success("Link copied");
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        <span className="label-mono">Admin</span>
        <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter mt-2">Partners</h1>
        <p className="text-[#52525B] mt-2">Review applications, approve referral codes, and track partner-driven growth.</p>

        <div className="flex gap-2 mt-8 flex-wrap">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`admin-partners-tab-${t}`}
              className={`btn-brutal px-4 py-2 text-sm capitalize ${tab === t ? "bg-blue-600 text-white" : "bg-white"}`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="mt-6 card-brutal overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FEF08A] border-b-2 border-[#0A0A0A]">
              <tr>
                <th className="text-left px-4 py-3 label-mono">Name</th>
                <th className="text-left px-4 py-3 label-mono">Email</th>
                <th className="text-left px-4 py-3 label-mono">Profession</th>
                <th className="text-left px-4 py-3 label-mono">Applied</th>
                <th className="text-left px-4 py-3 label-mono">Code</th>
                <th className="text-right px-4 py-3 label-mono">Signups</th>
                <th className="text-right px-4 py-3 label-mono">Paid</th>
                <th className="text-right px-4 py-3 label-mono">Revenue</th>
                <th className="text-right px-4 py-3 label-mono">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="9" className="px-4 py-6 label-mono">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan="9" data-testid="no-partners" className="px-4 py-6 text-[#52525B]">No {tab === "all" ? "" : tab} applications.</td></tr>
              ) : (
                items.map((p) => (
                  <tr key={p.id} data-testid={`partner-row-${p.id}`} className="border-b border-[#0A0A0A]/10 hover:bg-[#FAFAF9] align-top">
                    <td className="px-4 py-3 font-semibold">{p.full_name}</td>
                    <td className="px-4 py-3 text-[#52525B]">{p.email}<br /><span className="text-xs">{p.phone}</span></td>
                    <td className="px-4 py-3">{p.profession}</td>
                    <td className="px-4 py-3 text-[#52525B]">{p.created_at ? format(new Date(p.created_at), "dd MMM yyyy") : "—"}</td>
                    <td className="px-4 py-3">
                      {p.referral_code ? (
                        <button onClick={() => copyLink(p.referral_link)} className="font-mono text-xs flex items-center gap-1 hover:underline">
                          {p.referral_code} <Copy size={11} strokeWidth={2.5} />
                        </button>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">{p.signups}</td>
                    <td className="px-4 py-3 text-right">{p.paid_conversions}</td>
                    <td className="px-4 py-3 text-right">₹{p.revenue.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {p.has_pan_card && (
                          <button onClick={() => viewPan(p.id)} data-testid={`partner-pan-${p.id}`} className="btn-brutal bg-white px-2 py-2 text-xs" title="View PAN card">
                            <FileText size={12} strokeWidth={2.5} />
                          </button>
                        )}
                        {p.status === "pending" && (
                          <>
                            <button
                              onClick={() => approve(p.id)}
                              disabled={busyId === p.id}
                              data-testid={`partner-approve-${p.id}`}
                              className="btn-brutal bg-green-600 text-white px-2 py-2 text-xs disabled:opacity-60"
                              title="Approve"
                            >
                              {busyId === p.id ? <Loader2 className="animate-spin" size={12} /> : <Check size={12} strokeWidth={2.5} />}
                            </button>
                            <button
                              onClick={() => reject(p.id)}
                              disabled={busyId === p.id}
                              data-testid={`partner-reject-${p.id}`}
                              className="btn-brutal bg-white px-2 py-2 text-xs disabled:opacity-60"
                              title="Reject"
                            >
                              <X size={12} strokeWidth={2.5} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
